/**
 * Real-time, speaker-labelled transcription for the ambient scribe, using
 * the Azure Speech SDK's ConversationTranscriber against the clinic's own
 * Azure Speech resource (covered by the Azure BAA).
 *
 * The browser's built-in SpeechRecognition is deliberately NOT used: Chrome
 * sends that audio to Google and Edge to Microsoft's consumer service, with
 * no BAA. The subscription key never reaches the browser — the backend issues
 * 10-minute tokens, refreshed here before they expire.
 *
 * If the speech connection drops mid-visit, the microphone keeps running and
 * the transcriber reconnects on its own. Speech during the outage can't be
 * recovered, so the gap is reported and marked in the transcript rather than
 * silently missing.
 */
import {
  AudioConfig,
  CancellationReason,
  ConversationTranscriber,
  PhraseListGrammar,
  PropertyId,
  ResultReason,
  SpeechConfig,
} from "microsoft-cognitiveservices-speech-sdk";
import { getSpeechToken, type SpeechToken } from "@/api/ai.api";

export interface TranscriptLine {
  /** Azure's diarization label, e.g. "Guest-1"; "Unknown" before it settles. */
  speaker: string;
  text: string;
  /**
   * "gap": audio not transcribed live (connection lost), not speech.
   * "recovered": outage audio transcribed afterwards, without speaker labels.
   */
  kind?: "gap" | "recovered";
  /** A gap whose buffered audio is still being transcribed. */
  pending?: boolean;
}

export interface TranscriberCallbacks {
  /** A finished phrase. */
  onPhrase: (line: TranscriptLine) => void;
  /** The phrase currently being spoken (replaced as it firms up). */
  onInterim: (line: TranscriptLine | null) => void;
  /** Unrecoverable: reconnecting gave up. */
  onError: (message: string) => void;
  /** The speech connection dropped; reconnecting in the background. */
  onReconnecting?: () => void;
  /** Back online after a drop; `gapSeconds` of audio were not transcribed. */
  onReconnected?: (gapSeconds: number) => void;
}

export interface LiveTranscriber {
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

/** Refresh a minute before Azure's 10-minute token expires. */
const REFRESH_MARGIN_MS = 60_000;
const TOKEN_RETRY_MS = 15_000;
/** Backoff between reconnect attempts; the last delay repeats. */
export const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];
/** Give up after this long without a connection. */
export const RECONNECT_GIVE_UP_MS = 3 * 60_000;

/** "Guest-1" → "Speaker 1" — readable in the transcript and for the note model. */
export function speakerLabel(raw: string | undefined): string {
  const m = /(\d+)$/.exec(raw ?? "");
  return m ? `Speaker ${m[1]}` : "Speaker";
}

function spanText(gapSeconds: number): string {
  const secs = Math.max(1, Math.round(gapSeconds));
  return secs < 90 ? `about ${secs} seconds` : `about ${Math.round(secs / 60)} minutes`;
}

export function gapLine(gapSeconds: number, pending = false): TranscriptLine {
  const span = spanText(gapSeconds);
  return pending
    ? { speaker: "", kind: "gap", pending: true, text: `[Connection lost - recovering ${span} of audio...]` }
    : { speaker: "", kind: "gap", text: `[Connection lost - ${span} of the visit was not captured]` };
}

export function recoveredLine(text: string): TranscriptLine {
  return { speaker: "", kind: "recovered", text: `[Recovered after connection loss - speakers not labelled] ${text.trim()}` };
}

/** Joins finished lines into a transcript, merging consecutive lines from the same speaker. */
export function formatTranscript(lines: TranscriptLine[], interim?: TranscriptLine | null): string {
  const all = interim?.text ? [...lines, interim] : lines;
  const out: string[] = [];
  let current = "";
  let currentSpeaker = "";
  for (const line of all) {
    if (line.kind) {
      if (current) out.push(current);
      out.push(line.text);
      current = "";
      currentSpeaker = "";
      continue;
    }
    const speaker = speakerLabel(line.speaker);
    if (speaker === currentSpeaker && current) {
      current += ` ${line.text}`;
    } else {
      if (current) out.push(current);
      current = `${speaker}: ${line.text}`;
      currentSpeaker = speaker;
    }
  }
  if (current) out.push(current);
  return out.join("\n");
}

export async function createLiveTranscriber(
  stream: MediaStream,
  cb: TranscriberCallbacks,
  /** Terms to bias recognition toward (drug names, diagnoses); see buildScribePhrases. */
  phrases: string[] = [],
): Promise<LiveTranscriber> {
  let token: SpeechToken = await getSpeechToken();
  let transcriber: ConversationTranscriber | null = null;
  let refreshTimer: number | null = null;
  let reconnectTimer: number | null = null;
  let stopped = false;
  /** Reconnects only after the first start succeeded; a failed start is reported by start(). */
  let started = false;
  /** When the current outage began; null while connected. */
  let droppedAt: number | null = null;

  const build = (): ConversationTranscriber => {
    const speechConfig = SpeechConfig.fromAuthorizationToken(token.token, token.region);
    speechConfig.speechRecognitionLanguage = token.locale || "en-US";
    // Speaker labels on in-progress phrases too, so the live view doesn't flicker.
    speechConfig.setProperty(PropertyId.SpeechServiceResponse_DiarizeIntermediateResults, "true");
    const t = new ConversationTranscriber(speechConfig, AudioConfig.fromStreamInput(stream));
    if (phrases.length) PhraseListGrammar.fromRecognizer(t).addPhrases(phrases);

    t.transcribing = (_s, e) => {
      const text = e.result.text?.trim();
      cb.onInterim(text ? { speaker: e.result.speakerId, text } : null);
    };
    t.transcribed = (_s, e) => {
      if (e.result.reason !== ResultReason.RecognizedSpeech) return;
      const text = e.result.text?.trim();
      cb.onInterim(null);
      if (text) cb.onPhrase({ speaker: e.result.speakerId, text });
    };
    t.canceled = (_s, e) => {
      if (e.reason === CancellationReason.Error && started && !stopped && t === transcriber) handleDrop();
    };
    return t;
  };

  const closeCurrent = () => {
    const t = transcriber;
    transcriber = null;
    try {
      t?.close();
    } catch {
      /* already closed */
    }
  };

  const startCurrent = (t: ConversationTranscriber) =>
    new Promise<void>((resolve, reject) => {
      t.startTranscribingAsync(resolve, (err) => reject(new Error(String(err))));
    });

  const scheduleRefresh = (delay = Math.max(30_000, token.expires_in_seconds * 1000 - REFRESH_MARGIN_MS)) => {
    if (refreshTimer != null) window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(async () => {
      if (stopped) return;
      try {
        token = await getSpeechToken();
        if (transcriber) transcriber.authorizationToken = token.token;
        scheduleRefresh();
      } catch {
        // The open connection keeps working on the old token for now; if it
        // expires, the drop is handled by the reconnect loop.
        scheduleRefresh(TOKEN_RETRY_MS);
      }
    }, delay);
  };

  const handleDrop = () => {
    if (droppedAt != null) return;
    droppedAt = Date.now();
    cb.onInterim(null);
    cb.onReconnecting?.();
    closeCurrent();
    attemptReconnect(0);
  };

  const attemptReconnect = (attempt: number) => {
    if (stopped || droppedAt == null) return;
    if (Date.now() - droppedAt > RECONNECT_GIVE_UP_MS) {
      cb.onError("Lost connection to the speech service. Finish to draft from what was captured, or dictate the rest.");
      return;
    }
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]!;
    reconnectTimer = window.setTimeout(async () => {
      reconnectTimer = null;
      if (stopped) return;
      try {
        token = await getSpeechToken();
        if (stopped) return;
        const next = build();
        transcriber = next;
        await startCurrent(next);
        if (stopped) {
          // stop() ran while this attempt was connecting and didn't see it.
          closeCurrent();
          return;
        }
        const gapSeconds = (Date.now() - (droppedAt ?? Date.now())) / 1000;
        droppedAt = null;
        scheduleRefresh();
        cb.onReconnected?.(gapSeconds);
      } catch {
        closeCurrent();
        attemptReconnect(attempt + 1);
      }
    }, delay);
  };

  const clearTimers = () => {
    if (refreshTimer != null) window.clearTimeout(refreshTimer);
    if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
    refreshTimer = null;
    reconnectTimer = null;
  };

  return {
    start: async () => {
      const t = build();
      transcriber = t;
      try {
        await startCurrent(t);
      } catch {
        clearTimers();
        closeCurrent();
        throw new Error("Couldn't start transcription. Check your microphone and internet connection, then try again.");
      }
      started = true;
      scheduleRefresh();
    },
    // Stopping flushes the last phrase through `transcribed` before resolving.
    stop: () =>
      new Promise<void>((resolve) => {
        stopped = true;
        clearTimers();
        const t = transcriber;
        if (!t) {
          resolve();
          return;
        }
        const done = () => {
          closeCurrent();
          resolve();
        };
        const timer = window.setTimeout(done, 4000);
        t.stopTranscribingAsync(
          () => {
            window.clearTimeout(timer);
            done();
          },
          () => {
            window.clearTimeout(timer);
            done();
          },
        );
      }),
  };
}
